<template>
  <div class="sm:w-full md:w4/5 lg:w-3/5 mx-auto md:p-6">
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4">
        <h2 class="text-xl font-semibold text-white">Edit Friend</h2>
        <p class="text-red-100 text-sm mt-1">Update {{ friendForm.name || 'your friend' }}'s information</p>
      </div>

      <!-- Form Content -->
      <form @submit.prevent="updateFriend" class="p-6 space-y-6">
        <!-- Name Field -->
        <div>
          <label for="friendName" class="block text-sm font-medium text-gray-700 mb-2">
            Friend Name
          </label>
          <input
            v-model="friendForm.name"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-gray-50 hover:bg-white"
            id="friendName"
            placeholder="Enter their name"
            maxlength="100"
>
        </div>

        <!-- Email Field -->
        <div>
          <label for="friendEmail" class="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            v-model="friendForm.email"
            type="email"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-gray-50 hover:bg-white"
            id="friendEmail"
            placeholder="their@email.com"
            maxlength="254"
>
        </div>

        <!-- Birthday Section -->
        <div class="bg-pink-50 rounded-lg p-4 border border-pink-200">
          <div class="flex items-center mb-3">
            <div class="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-pink-600">
                <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" /><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" /><path d="M2 21h20" /><path d="M7 8v3" /><path d="M12 8v3" /><path d="M17 8v3" /><path d="M7 4h.01" /><path d="M12 4h.01" /><path d="M17 4h.01" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900">Birthday</h3>
          </div>
          
          <div class="mb-4">
            <label for="friendBirthday" class="block text-sm font-medium text-gray-700 mb-2">
              Birthday Date
            </label>
            <input 
              v-model="friendForm.birthday" 
              type="date"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-white"
              id="friendBirthday"
>
          </div>

          <div class="flex items-start space-x-3">
            <input 
              id="reminder" 
              type="checkbox" 
              v-model="friendForm.reminder" 
              :disabled="birthdaySet == false"
              class="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
>
            <div class="flex-1">
              <label 
                for="reminder"
                :class="birthdaySet ? 'text-gray-900' : 'text-gray-400'"
                class="block text-sm font-medium"
>
                Remind me before their birthday
              </label>
              <p class="text-xs text-gray-500 mt-1">
                We'll send you a reminder 2 weeks before their birthday
              </p>
            </div>
          </div>
        </div>

        <!-- Anniversary Section -->
        <div class="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div class="flex items-center mb-3">
            <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-purple-600">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900">Anniversary</h3>
          </div>
          
          <div class="mb-4">
            <label for="friendAnniversary" class="block text-sm font-medium text-gray-700 mb-2">
              Anniversary Date
            </label>
            <input 
              v-model="friendForm.anniversary" 
              type="date"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-white"
              id="friendAnniversary"
>
          </div>

          <div class="flex items-start space-x-3">
            <input 
              id="reminderAnniversary" 
              type="checkbox" 
              v-model="friendForm.anniversaryReminder"
              :disabled="anniversarySet == false" 
              class="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
>
            <div class="flex-1">
              <label 
                for="reminderAnniversary"
                :class="anniversarySet ? 'text-gray-900' : 'text-gray-400'"
                class="block text-sm font-medium"
>
                Remind me before this anniversary
              </label>
              <p class="text-xs text-gray-500 mt-1">
                We'll send you a reminder 2 weeks before your anniversary
              </p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button 
            type="button" 
            @click="$router.go(-1)"
            class="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors duration-200"
>
            Cancel
          </button>
          <button 
            @click="updateFriend" 
            type="button"
            class="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
>
            Save Changes
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { API } from 'aws-amplify';

import { updateFriend } from '../graphql/mutations';
import { deleteGiftFriend } from '../graphql/customMutations';

export default {
  name: "FriendEdit",
  props: {
    friend: {
      type: Object,
      default() {
        return {}
      }
    },
  },
  components: {
  },
  computed: {
    birthdaySet() {
      return this.friendForm.birthday ? true : false;
    },
    anniversarySet() {
      return this.friendForm.anniversary ? true : false;
    }

  },
  created() {
    this.friendForm = { ...this.friend }
  },
  data() {
    return {
      friendForm: {},
    };
  },
  methods: {
    async deleteGiftFriend(giftLinkId, giftLinkVersion) {
      const mutationInput = { "id": giftLinkId };
      try {
        const response = await API.graphql({ query: deleteGiftFriend, variables: { input: mutationInput }, });
      } catch (error) {
        console.log('Error deleting gift...', error)
      }
    },
    async updateFriend() {
      const friendData = {
        id: this.friend.id,
        name: this.friendForm.name,
        email: this.friendForm.email,
        birthday: this.friendForm.birthday,
        reminder: this.friendForm.reminder,
        anniversary: this.friendForm.anniversary,
        anniversaryReminder: this.friendForm.anniversaryReminder,
      };
      try {
        const response = await API.graphql({ query: updateFriend, variables: { input: friendData } });
        this.$router.go(-1)
      } catch (error) {
        console.log('Error creating gift...', error)
      }
    },
  },
};
</script>